// @ts-nocheck
/**
 * ツインキーシステム - パッシブ解決ロジック
 *
 * カード情報からパッシブセットを決定する
 * 優先順位: 固有 → 属性×カテゴリグループ汎用
 *
 * 仕様書: twin_key_generic_skills_passives_v1.md
 */

import { getCategoryGroup, CATEGORY_GROUPS } from '../categoryMapping';
import { getAttributePassives } from './attributePassives';
import { getUniquePassives, hasUniquePassives } from './uniquePassives';
import { getUltimatePassive } from './ultimatePassives';

// ========================================
// パッシブ解放条件
// ========================================

/**
 * パッシブ解放覚醒レベル定義
 * ★0 = 初期、★1-3 = 覚醒
 */
export const PASSIVE_UNLOCK_AWAKENING = {
  1: 0,  // パッシブ1: ★0（初期）
  2: 1,  // パッシブ2: ★1
  3: 2,  // パッシブ3: ★2
  4: 3,  // 究極パッシブ: ★3
};

// ========================================
// パッシブ解決メイン関数
// ========================================

/**
 * カード情報からパッシブセットを決定
 *
 * 優先順位:
 * 1. 固有パッシブ持ち → 固有パッシブを使用
 * 2. それ以外 → 属性×カテゴリグループパッシブを使用
 *
 * @param {Object} card - カードデータ
 * @param {string} card.id - カードID
 * @param {string} card.attribute - 属性
 * @param {string} card.category - カテゴリ
 * @returns {Object} パッシブ解決結果
 */
export const resolvePassives = (card) => {
  if (!card) {
    return {
      passives: [],
      source: 'none',
      sourceDetail: null,
    };
  }

  const { id, attribute, category } = card;

  // 1. 固有パッシブチェック
  if (hasUniquePassives(id)) {
    const uniquePassives = getUniquePassives(id);
    return {
      passives: uniquePassives,
      source: 'unique',
      sourceDetail: { cardId: id },
    };
  }

  // 2. 属性×カテゴリグループパッシブ
  const categoryGroup = getCategoryGroup(category);
  const passives = getAttributePassives(attribute, categoryGroup);

  return {
    passives: passives,
    source: 'generic',
    sourceDetail: { attribute, categoryGroup },
  };
};

/**
 * パッシブ解決結果からパッシブ配列のみを取得
 * @param {Object} card - カードデータ
 * @returns {Object[]} パッシブ配列
 */
export const getPassivesForCard = (card) => {
  const result = resolvePassives(card);
  return result.passives;
};

/**
 * カードのパッシブソース情報を取得
 * UIでパッシブの由来を表示する際に使用
 * @param {Object} card - カードデータ
 * @returns {Object} { source, sourceDetail }
 */
export const getPassiveSource = (card) => {
  const result = resolvePassives(card);
  return {
    source: result.source,
    sourceDetail: result.sourceDetail,
  };
};

/**
 * パッシブソースの表示名を取得
 * @param {string} source - パッシブソース
 * @param {Object} sourceDetail - ソース詳細
 * @returns {string} 表示名
 */
export const getPassiveSourceDisplayName = (source, sourceDetail) => {
  if (source === 'unique') {
    return '固有パッシブ';
  }

  if (source === 'generic' && sourceDetail) {
    const groupNames = {
      [CATEGORY_GROUPS.ATTACKER]: 'アタッカー',
      [CATEGORY_GROUPS.TANK]: 'タンク',
      [CATEGORY_GROUPS.SPEED]: 'スピード',
      [CATEGORY_GROUPS.SUPPORT]: 'サポート',
      [CATEGORY_GROUPS.BALANCE]: 'バランス',
      [CATEGORY_GROUPS.SPECIAL]: '特殊',
    };
    const groupName = groupNames[sourceDetail.categoryGroup] || sourceDetail.categoryGroup;
    return `${sourceDetail.attribute}×${groupName}`;
  }

  return '汎用パッシブ';
};

// ========================================
// パッシブ解放判定
// ========================================

/**
 * 覚醒レベルに応じたパッシブ解放状態を取得
 * @param {number} awakening - 覚醒レベル（0-5）
 * @returns {Object} { passive1: boolean, passive2: boolean, passive3: boolean, ultimate: boolean }
 */
export const getPassiveUnlockStatus = (awakening) => {
  return {
    passive1: awakening >= PASSIVE_UNLOCK_AWAKENING[1],  // ★0以上（常にtrue）
    passive2: awakening >= PASSIVE_UNLOCK_AWAKENING[2],  // ★1以上
    passive3: awakening >= PASSIVE_UNLOCK_AWAKENING[3],  // ★2以上
    ultimate: awakening >= PASSIVE_UNLOCK_AWAKENING[4],  // ★3以上
  };
};

/**
 * パッシブが解放されているか確認
 * @param {number} slot - パッシブスロット（1-4）
 * @param {number} awakening - 覚醒レベル
 * @returns {boolean} 解放されていればtrue
 */
export const isPassiveUnlocked = (slot, awakening) => {
  const requiredAwakening = PASSIVE_UNLOCK_AWAKENING[slot];
  return awakening >= requiredAwakening;
};

/**
 * 次のパッシブ解放に必要な覚醒レベルを取得
 * @param {number} awakening - 現在の覚醒レベル
 * @returns {number|null} 次の解放に必要な覚醒レベル、全解放済みならnull
 */
export const getNextPassiveUnlockAwakening = (awakening) => {
  for (const [slot, requiredAwakening] of Object.entries(PASSIVE_UNLOCK_AWAKENING)) {
    if (awakening < requiredAwakening) {
      return requiredAwakening;
    }
  }
  return null;
};

// ========================================
// パッシブインスタンス生成
// ========================================

/**
 * モンスターインスタンス用のパッシブ配列を生成
 * 解放状態を含む
 *
 * @param {Object} card - カードデータ
 * @param {number} awakening - 覚醒レベル（0-5）
 * @returns {Object[]} パッシブインスタンス配列
 */
export const createPassiveInstances = (card, awakening) => {
  const { passives } = resolvePassives(card);
  const unlockStatus = getPassiveUnlockStatus(awakening);

  return passives.map((passive, index) => {
    const slotNumber = index + 1;
    const unlockKey = slotNumber === 4 ? 'ultimate' : `passive${slotNumber}`;
    const unlocked = unlockStatus[unlockKey];

    return {
      ...passive,
      slotNumber,
      unlocked,
      // 究極パッシブの場合、詳細情報を追加
      ultimateData: passive.isUltimate && passive.ultimateId
        ? getUltimatePassive(passive.ultimateId)
        : null,
    };
  });
};

/**
 * 有効なパッシブのみを取得
 * 解放済みパッシブのみ返す
 *
 * @param {Object[]} passiveInstances - パッシブインスタンス配列
 * @returns {Object[]} 有効なパッシブ配列
 */
export const getActivePassives = (passiveInstances) => {
  return passiveInstances.filter(passive => passive.unlocked);
};

/**
 * パッシブ効果を収集
 * 特定のタイプの効果をすべて取得
 *
 * @param {Object[]} passiveInstances - パッシブインスタンス配列
 * @param {string} effectType - 効果タイプ
 * @returns {Object[]} マッチした効果配列
 */
export const collectPassiveEffects = (passiveInstances, effectType) => {
  const activePassives = getActivePassives(passiveInstances);
  const effects = [];

  for (const passive of activePassives) {
    for (const effect of passive.effects || []) {
      if (effect.type === effectType) {
        effects.push({
          ...effect,
          passiveId: passive.id,
          passiveName: passive.name,
        });
      }
    }
  }

  return effects;
};

/**
 * パッシブからステータス修正値を計算
 *
 * @param {Object[]} passiveInstances - パッシブインスタンス配列
 * @param {string} statType - ステータスタイプ（'atk_percent', 'hp_percent', etc.）
 * @returns {number} 合計修正値
 */
export const calculatePassiveStatBonus = (passiveInstances, statType) => {
  const effects = collectPassiveEffects(passiveInstances, statType);
  return effects.reduce((sum, effect) => sum + (effect.value || 0), 0);
};

// ========================================
// エクスポート
// ========================================

export default {
  PASSIVE_UNLOCK_AWAKENING,
  resolvePassives,
  getPassivesForCard,
  getPassiveSource,
  getPassiveSourceDisplayName,
  getPassiveUnlockStatus,
  isPassiveUnlocked,
  getNextPassiveUnlockAwakening,
  createPassiveInstances,
  getActivePassives,
  collectPassiveEffects,
  calculatePassiveStatBonus,
};
